import React, { useEffect } from 'react'
import { Stack, TextField, FormHelperText, Typography, useMediaQuery, useTheme } from '@mui/material'
import Lottie from 'lottie-react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ecommerceOutlookAnimation } from '../assets'
import { useDispatch, useSelector } from 'react-redux'
import { LoadingButton } from '@mui/lab'
import { selectLoggedInUser, loginAsync, selectLoginStatus, selectLoginError, clearLoginError, resetLoginStatus, logoutAsync } from '../features/auth/AuthSlice'
import { toast } from 'react-toastify'

export const AdminLoginPage = () => {
  const dispatch = useDispatch()
  const status = useSelector(selectLoginStatus)
  const error = useSelector(selectLoginError)
  const loggedInUser = useSelector(selectLoggedInUser)
  const { register, handleSubmit, reset, formState: { errors } } = useForm()
  const navigate = useNavigate()
  const theme = useTheme()
  const is900 = useMediaQuery(theme.breakpoints.down(900))

  useEffect(() => {
    if (loggedInUser && loggedInUser?.isAdmin) {
      navigate('/admin/panel')
    } else if (loggedInUser && !loggedInUser?.isAdmin) {
      // If a non-admin accidentally logged in here, log them out and show error
      dispatch(logoutAsync())
      toast.error('You are not an admin')
    }
  }, [loggedInUser, navigate, dispatch])

  useEffect(() => {
    if (error) toast.error(error.message)
    return () => {
      dispatch(clearLoginError())
      dispatch(resetLoginStatus())
    }
  }, [error, dispatch])

  const handleAdminLogin = async (data) => {
    const cred = { ...data }
    delete cred.confirmPassword
    try {
      const res = await dispatch(loginAsync(cred)).unwrap()
      if (res && res.isAdmin) {
        toast.success('Admin login successful')
        reset()
        navigate('/admin/panel')
      } else {
        // ensure no non-admin session remains
        await dispatch(logoutAsync())
        toast.error('Invalid admin credentials')
      }
    } catch (err) {
      // login error will be shown from error state
    }
  }

  return (
    <Stack width={'100vw'} height={'100vh'} flexDirection={'row'} sx={{ overflowY: 'hidden' }}>
      {!is900 && (
        <Stack bgcolor={'black'} flex={1} justifyContent={'center'}>
          <Lottie animationData={ecommerceOutlookAnimation} />
        </Stack>
      )}

      <Stack flex={1} justifyContent={'center'} alignItems={'center'}>
        <Stack flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
          <Stack rowGap={'.4rem'}>
            <Typography variant='h2' sx={{ wordBreak: 'break-word' }} fontWeight={600}>Admin Login</Typography>
            <Typography alignSelf={'flex-end'} color={'GrayText'} variant='body2'>Admin portal</Typography>
          </Stack>
        </Stack>

        <Stack mt={4} spacing={2} width={'28rem'} component={'form'} noValidate onSubmit={handleSubmit(handleAdminLogin)}>
          <TextField fullWidth {...register('email', { required: 'Email is required' })} placeholder='Email' />
          {errors.email && <FormHelperText sx={{ mt: 1 }} error>{errors.email.message}</FormHelperText>}
          <TextField type='password' fullWidth {...register('password', { required: 'Password is required' })} placeholder='Password' />
          {errors.password && <FormHelperText sx={{ mt: 1 }} error>{errors.password.message}</FormHelperText>}

          <LoadingButton fullWidth sx={{ height: '2.5rem' }} loading={status === 'pending'} type='submit' variant='contained'>Login</LoadingButton>

          <Typography sx={{ textDecoration: 'none', color: 'text.primary' }} to={'/'} component={Link}>Back to store</Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default AdminLoginPage
